import React from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../../../common/types';
import { lyricPanelMessages } from './lyricPanelMessages';
import type { LyricRowState } from './useLyricPick';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the lyric panel; each part uses its own slice of it */
import styles from './LyricLinePanel.module.scss';
/* eslint-enable css-modules/no-unused-class */

export interface LyricRowTag {
  text: string;
  /** True: a grey tag (a count, a section). False: the brand tag (Hook, Short). */
  grey: boolean;
}

interface LyricLineRowProps {
  line: string;
  state: LyricRowState;
  onPick: () => void;
  tags?: ReadonlyArray<LyricRowTag>;
  /** The section name of a search hit. */
  section?: string;
  /** The start of the search match in the line. */
  matchStart?: number;
  matchLength?: number;
}

const STATE_CLASS: Record<LyricRowState, string | undefined> = {
  off: undefined,
  on: styles.rowOn,
  pairFirst: styles.rowPairFirst,
  pairSecond: styles.rowPairSecond,
};

function LineText({ line, start, length }: { line: string; start?: number; length?: number }): React.ReactElement {
  if (start === undefined || length === undefined || start < 0 || length <= 0) {
    return <>{line}</>;
  }
  return (
    <>
      {line.slice(0, start)}
      <mark className={styles.mark}>{line.slice(start, start + length)}</mark>
      {line.slice(start + length)}
    </>
  );
}

/** One line of the panel: a radio, the text, the tags and the number of characters. */
const LyricLineRowComponent: React.FC<LyricLineRowProps> = ({
  line,
  state,
  onPick,
  tags,
  section,
  matchStart,
  matchLength,
}) => {
  const intl = useIntl();
  const picked = state !== 'off';
  const inPreview = state === 'on' || state === 'pairSecond';
  const className = [styles.row, STATE_CLASS[state]].filter((name) => name !== undefined).join(' ');
  return (
    <button type="button" role="radio" aria-checked={picked} className={className} onClick={onPick} data-lyric-row>
      <span className={styles.radio} aria-hidden="true" />
      <span className={styles.rowText}>
        <LineText line={line} start={matchStart} length={matchLength} />
      </span>
      {section !== undefined && <span className={styles.rowSection}>{section}</span>}
      {tags?.map((tag) => (
        <span key={tag.text} className={tag.grey ? styles.tagGrey : styles.tag}>
          {tag.text}
        </span>
      ))}
      {inPreview && <span className={styles.inPreview}>{intl.formatMessage(lyricPanelMessages.inPreview)}</span>}
      <span className={styles.rowCount}>{line.length}</span>
    </button>
  );
};

export const LyricLineRow = TypedMemo(LyricLineRowComponent);
